import React, { useContext, useEffect, useState } from "react";

import {
    Container,
    LabelWrapper,
    Label,
    InputRow,
    InputContainer,
    EyeButton,
    EditButton,
    EditIcon,
    InputField,
    ActionButton,
    ActionButtonGroup,
    InfoIconWrapper,
    InfoIcon,
    InfoTooltip,
} from "./IpAddress.styles";

import EyeSvg from "../../../../../../images/printers/eye.svg";
import CloseEyeSvg from "../../../../../../images/printers/close_eye.svg";
import EditSvg from "../../../../../../images/printers/edit.svg";
import CheckSvg from "../../../../../../images/printers/check.svg";
import CrossSvg from "../../../../../../images/printers/cross.svg";

import { updatePrinterIpAddress } from "../../../../../../zeus-client/gatewaysApis";
import { Notifier } from "../../../../../../view/Notifier";
import { ApplicationContext } from "../../../../../../components/ApplicationProvider";

interface IpAddressProps {
    clientDeviceThingName?: string;
    ipAddress?: string;
}

const IPv4_REGEX =
    /^(?:(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.){3}(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)$/;

const MASKED_IP_ADDRESS = "•••••••••••••••";

const IpAddress: React.FC<IpAddressProps> = ({ clientDeviceThingName, ipAddress }) => {
    const { t } = useContext(ApplicationContext);
    const resolvedIpAddress = ipAddress ?? "";
    const ipAddressUpdateSuccessMessage = t("devices.printer_card.ip_address_update_success");
    const noIpAddressPlaceholder = t("devices.printer_card.ip_address_not_available");

    const [showIp, setShowIp] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [currentIpAddress, setCurrentIpAddress] = useState(resolvedIpAddress);
    const [draftIpAddress, setDraftIpAddress] = useState(resolvedIpAddress);
    const [isSaving, setIsSaving] = useState(false);
    const hasIpAddress = Boolean(currentIpAddress.trim());

    useEffect(() => {
        setCurrentIpAddress(resolvedIpAddress);
        setDraftIpAddress(resolvedIpAddress);
    }, [resolvedIpAddress]);

    const beginEdit = () => {
        setDraftIpAddress(currentIpAddress);
        setIsEditing(true);
    };

    const cancelEdit = () => {
        setDraftIpAddress(currentIpAddress);
        setIsEditing(false);
    };

    const handleSave = async () => {
        const trimmedValue = draftIpAddress.trim();

        if (!trimmedValue) {
            Notifier.error(new Error("IP address cannot be empty."));
            return;
        }

        if (!IPv4_REGEX.test(trimmedValue)) {
            Notifier.error(new Error("Please enter a valid IPv4 address."));
            return;
        }

        if (!clientDeviceThingName) {
            setCurrentIpAddress(trimmedValue);
            setDraftIpAddress(trimmedValue);
            setIsEditing(false);
            Notifier.success(ipAddressUpdateSuccessMessage);
            return;
        }

        try {
            setIsSaving(true);

            const updatedDevice: Awaited<ReturnType<typeof updatePrinterIpAddress>> =
                await updatePrinterIpAddress(clientDeviceThingName, trimmedValue);

            const nextIpAddress = updatedDevice.ipAddress ?? trimmedValue;

            setCurrentIpAddress(nextIpAddress);
            setDraftIpAddress(nextIpAddress);
            setIsEditing(false);
            Notifier.success(ipAddressUpdateSuccessMessage);
        } catch (error) {
            Notifier.error(
                error instanceof Error ? error : new Error("Failed to update IP address.")
            );
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Container>
            <LabelWrapper>
                <Label data-testid="ip-address-label">{t("devices.printer_card.ip_address")}</Label>

                <InfoIconWrapper>
                    <InfoIcon name="info circle" size="large" aria-label="IP address information" />

                    <InfoTooltip role="tooltip">
                        {t("devices.printer_card.ip_address_tooltip")}
                    </InfoTooltip>
                </InfoIconWrapper>
            </LabelWrapper>

            <InputRow>
                <InputContainer data-testid="printer-ip-input">
                    <InputField
                        aria-label={isEditing ? "Edit IP address" : "IP address"}
                        value={
                            isEditing
                                ? draftIpAddress
                                : !hasIpAddress
                                ? noIpAddressPlaceholder
                                : showIp
                                ? currentIpAddress
                                : MASKED_IP_ADDRESS
                        }
                        onChange={(event) => setDraftIpAddress(event.target.value)}
                        readOnly={!isEditing}
                        disabled={isSaving}
                        $masked={!isEditing && !showIp}
                    />

                    {isEditing ? (
                        <ActionButtonGroup>
                            <ActionButton
                                type="button"
                                onClick={handleSave}
                                disabled={isSaving}
                                aria-label={isSaving ? "Saving IP address" : "Save IP address"}
                            >
                                {isSaving ? (
                                    "..."
                                ) : (
                                    <EditIcon src={CheckSvg} alt="Save IP address" />
                                )}
                            </ActionButton>
                            <ActionButton
                                type="button"
                                onClick={cancelEdit}
                                disabled={isSaving}
                                aria-label="Cancel IP address edit"
                            >
                                <EditIcon src={CrossSvg} alt="Cancel IP address edit" />
                            </ActionButton>
                        </ActionButtonGroup>
                    ) : (
                        <>
                            {hasIpAddress && (
                                <EyeButton
                                    type="button"
                                    onClick={() => setShowIp((previous) => !previous)}
                                    aria-label={showIp ? "Hide IP address" : "Show IP address"}
                                >
                                    {showIp ? (
                                        <EditIcon src={CloseEyeSvg} alt="Hide IP address" />
                                    ) : (
                                        <EditIcon src={EyeSvg} alt="Show IP address" />
                                    )}
                                </EyeButton>
                            )}
                            <EditButton
                                type="button"
                                onClick={beginEdit}
                                aria-label="Edit IP address"
                            >
                                <EditIcon src={EditSvg} alt="Edit IP address" />
                            </EditButton>
                        </>
                    )}
                </InputContainer>
            </InputRow>
        </Container>
    );
};

export default IpAddress;
